import type { LucideIcon } from 'lucide-react';
import {
  File, FileArchive, FileCode, FileImage, FileSpreadsheet, FileText, FileVideo,
  Folder, NotebookPen, Presentation,
} from 'lucide-react';

export type FileKind =
  | 'pdf' | 'doc' | 'sheet' | 'slide' | 'folder' | 'note'
  | 'image' | 'video' | 'archive' | 'code' | 'generic';

export interface FileKindMeta {
  icon: LucideIcon;
  /** Tailwind text color class for the glyph. */
  colorClass: string;
  /** Tailwind background class for the tinted glyph tile. */
  tileClass: string;
}

export const FILE_KIND_META: Record<FileKind, FileKindMeta> = {
  pdf: { icon: FileText, colorClass: 'text-file-pdf', tileClass: 'bg-file-pdf/10' },
  doc: { icon: FileText, colorClass: 'text-file-doc', tileClass: 'bg-file-doc/10' },
  sheet: { icon: FileSpreadsheet, colorClass: 'text-file-sheet', tileClass: 'bg-file-sheet/10' },
  slide: { icon: Presentation, colorClass: 'text-file-slide', tileClass: 'bg-file-slide/10' },
  folder: { icon: Folder, colorClass: 'text-file-folder', tileClass: 'bg-file-folder/10' },
  note: { icon: NotebookPen, colorClass: 'text-file-note', tileClass: 'bg-file-note/10' },
  image: { icon: FileImage, colorClass: 'text-file-image', tileClass: 'bg-file-image/10' },
  video: { icon: FileVideo, colorClass: 'text-file-video', tileClass: 'bg-file-video/10' },
  archive: { icon: FileArchive, colorClass: 'text-file-archive', tileClass: 'bg-file-archive/10' },
  code: { icon: FileCode, colorClass: 'text-file-code', tileClass: 'bg-file-code/10' },
  generic: { icon: File, colorClass: 'text-file-generic', tileClass: 'bg-file-generic/10' },
};

const EXT_MAP: Record<string, FileKind> = {
  pdf: 'pdf',
  doc: 'doc', docx: 'doc', rtf: 'doc', txt: 'doc', md: 'doc',
  xls: 'sheet', xlsx: 'sheet', csv: 'sheet',
  ppt: 'slide', pptx: 'slide', key: 'slide',
  one: 'note', note: 'note',
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', webp: 'image', svg: 'image',
  mp4: 'video', mov: 'video', webm: 'video', mkv: 'video',
  zip: 'archive', rar: 'archive', '7z': 'archive', tar: 'archive', gz: 'archive',
  ts: 'code', tsx: 'code', js: 'code', jsx: 'code', json: 'code', py: 'code', go: 'code', sql: 'code',
};

/** Resolve a display kind from a file name. Falls back to `generic`. */
export function fileKindFor(fileName: string): FileKind {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  return EXT_MAP[ext] ?? 'generic';
}
